import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Input,
  Label,
} from 'reactstrap';

import {
  SimpleTableWithSorting,
  TableCellProps,
  TableDateFormatHelper,
  generateColumn,
  getRTOriginal,
} from 'app/components/common';
import APIService from 'services/api';
import { logError } from 'utils/log';

interface IBombardinoSmsCustomer {
  CustomerId: number;
  name: string | null;
  surname: string | null;
  phone: string;
  OrdersAmount: number;
  OrdersSum: number | string;
  FirstOrder: string | null;
  LastOrder: string | null;
  CustomerSource: string | null;
}

const DEFAULT_MIN_DATE = '2024-01-01';

type ResultMode = 'customers' | 'phones';

// Hidden page - reachable only by URL, not present in nav.
// REST wrapper over web-app MarketingHelper (previously used via artisan tinker).
const AdminMarketingPage = () => {
  const [minDate, setMinDate] = useState(DEFAULT_MIN_DATE);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultMode, setResultMode] = useState<ResultMode | null>(null);
  const [customers, setCustomers] = useState<IBombardinoSmsCustomer[]>([]);
  const [phones, setPhones] = useState<string[]>([]);

  const buildUrl = (endpoint: string) => {
    const query = minDate ? `?min_date=${encodeURIComponent(minDate)}` : '';
    return `/admin/marketing/${endpoint}${query}`;
  };

  const loadCustomers = () => {
    setIsLoading(true);
    setError(null);
    APIService.get(buildUrl('bombardino-sms-customers'))
      .then((data: IBombardinoSmsCustomer[]) => {
        setCustomers(data);
        setResultMode('customers');
      })
      .catch((e) => {
        logError('AdminMarketingPage: failed to load sms customers', e);
        setError('Nie udało się pobrać klientów.');
      })
      .finally(() => setIsLoading(false));
  };

  const loadPhones = () => {
    setIsLoading(true);
    setError(null);
    APIService.get(buildUrl('bombardino-sms-phone-numbers'))
      .then((data: string[]) => {
        setPhones(data);
        setResultMode('phones');
      })
      .catch((e) => {
        logError('AdminMarketingPage: failed to load sms phone numbers', e);
        setError('Nie udało się pobrać numerów telefonów.');
      })
      .finally(() => setIsLoading(false));
  };

  const phonesText = useMemo(() => phones.join('\n'), [phones]);

  const columns = useMemo(
    () => [
      generateColumn('CustomerId', {
        customHeader: 'ID',
        filterType: 'none',
      }),
      generateColumn('name', {
        customHeader: 'Imię',
        filterType: 'text',
      }),
      generateColumn('surname', {
        customHeader: 'Nazwisko',
        filterType: 'text',
      }),
      generateColumn('phone', {
        customHeader: 'Telefon',
        filterType: 'text',
      }),
      generateColumn('OrdersAmount', {
        customHeader: 'Liczba zamówień',
        filterType: 'none',
      }),
      generateColumn('OrdersSum', {
        customHeader: 'Suma zamówień',
        filterType: 'none',
        customCellFormatter: (props: TableCellProps<IBombardinoSmsCustomer>) =>
          Number(getRTOriginal<IBombardinoSmsCustomer>(props).OrdersSum).toFixed(
            2
          ),
      }),
      generateColumn('FirstOrder', {
        customHeader: 'Pierwsze zamówienie',
        filterType: 'none',
        customCellFormatter: TableDateFormatHelper<IBombardinoSmsCustomer>(
          'FirstOrder',
          'dd.MM.yyyy'
        ),
      }),
      generateColumn('LastOrder', {
        customHeader: 'Ostatnie zamówienie',
        filterType: 'none',
        customCellFormatter: TableDateFormatHelper<IBombardinoSmsCustomer>(
          'LastOrder',
          'dd.MM.yyyy'
        ),
      }),
      generateColumn('CustomerSource', {
        customHeader: 'Źródło',
        filterType: 'select',
      }),
    ],
    []
  );

  return (
    <div className="container-fluid">
      <h2>Marketing – Bombardino SMS</h2>
      {error && <Alert color="danger">{error}</Alert>}
      <Card className="mb-3">
        <CardHeader>Klienci Bombardino ze zgodą na SMS marketing</CardHeader>
        <CardBody>
          <Form onSubmit={(e) => e.preventDefault()}>
            <FormGroup>
              <Label for="marketing-min-date">
                Minimalna data zamówienia (min_date)
              </Label>
              <Input
                id="marketing-min-date"
                type="date"
                value={minDate}
                onChange={(e) => setMinDate(e.target.value)}
                style={{ maxWidth: 240 }}
              />
              <small className="text-muted">
                Puste = domyślnie {DEFAULT_MIN_DATE}. Liczone są tylko
                dostarczone zamówienia z tenanta otostolik.
              </small>
            </FormGroup>
            <Button
              color="primary"
              className="mr-2"
              disabled={isLoading}
              onClick={loadCustomers}
            >
              Pobierz klientów
            </Button>
            <Button color="secondary" disabled={isLoading} onClick={loadPhones}>
              Pobierz numery telefonów
            </Button>
          </Form>
        </CardBody>
      </Card>

      {isLoading && <div>Wczytywanie...</div>}

      {!isLoading && resultMode === 'customers' && (
        <>
          <p>Liczba klientów: {customers.length}</p>
          <SimpleTableWithSorting
            data={customers}
            columns={columns}
            hasFilters
            pageSize={50}
          />
        </>
      )}

      {!isLoading && resultMode === 'phones' && (
        <FormGroup>
          <Label for="marketing-phones">
            Numery telefonów ({phones.length}) – jeden na linię
          </Label>
          <Input
            id="marketing-phones"
            type="textarea"
            rows={20}
            className="text-monospace"
            readOnly
            value={phonesText}
            onFocus={(e) => e.target.select()}
          />
        </FormGroup>
      )}
    </div>
  );
};

AdminMarketingPage.url = '/admin/marketing';
AdminMarketingPage.navName = 'admin.marketing';

export default AdminMarketingPage;
